import React, { useState, useEffect } from 'react';
import styles from './OpeningHoursEditor.module.css';

interface DayHours {
  open: string;
  close: string;
  closed: boolean;
  lunchBreak?: {
    start: string;
    end: string;
  };
}

interface SpecialPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  hours: {
    monday: DayHours;
    tuesday: DayHours;
    wednesday: DayHours;
    thursday: DayHours;
    friday: DayHours;
    saturday: DayHours;
    sunday: DayHours;
  };
}

interface OpeningHours {
  regular: {
    monday: DayHours;
    tuesday: DayHours;
    wednesday: DayHours;
    thursday: DayHours;
    friday: DayHours;
    saturday: DayHours;
    sunday: DayHours;
  };
  special?: SpecialPeriod[];
}

interface OpeningHoursEditorProps {
  value: string; // JSON string
  onChange: (value: string) => void;
}

const DAYS = [
  { key: 'monday', label: 'Lunedì' },
  { key: 'tuesday', label: 'Martedì' },
  { key: 'wednesday', label: 'Mercoledì' },
  { key: 'thursday', label: 'Giovedì' },
  { key: 'friday', label: 'Venerdì' },
  { key: 'saturday', label: 'Sabato' },
  { key: 'sunday', label: 'Domenica' },
] as const;

const defaultDayHours: DayHours = {
  open: '09:00',
  close: '18:00',
  closed: false,
};

const defaultRegularHours = {
  monday: { ...defaultDayHours },
  tuesday: { ...defaultDayHours },
  wednesday: { ...defaultDayHours },
  thursday: { ...defaultDayHours },
  friday: { ...defaultDayHours },
  saturday: { open: '10:00', close: '14:00', closed: false },
  sunday: { open: '09:00', close: '18:00', closed: true },
};

export const OpeningHoursEditor: React.FC<OpeningHoursEditorProps> = ({ value, onChange }) => {
  const [hours, setHours] = useState<OpeningHours>({ regular: defaultRegularHours, special: [] });
  const [showSpecialPeriods, setShowSpecialPeriods] = useState(false);
  const [editingSpecialIndex, setEditingSpecialIndex] = useState<number | null>(null);

  // Parse JSON value on mount and when it changes
  useEffect(() => {
    if (value) {
      try {
        const parsed = JSON.parse(value);
        // Handle old format (direct day hours) vs new format (with regular/special)
        if (parsed.regular) {
          setHours(parsed);
        } else {
          // Convert old format to new format
          setHours({ regular: parsed, special: [] });
        }
      } catch (e) {
        console.warn('Failed to parse opening hours:', e);
      }
    }
  }, [value]);

  const updateHours = (newHours: OpeningHours) => {
    setHours(newHours);
    onChange(JSON.stringify(newHours));
  };

  const handleDayChange = (
    section: 'regular' | number,
    day: string,
    field: keyof DayHours | 'lunchBreak.start' | 'lunchBreak.end',
    newValue: string | boolean
  ) => {
    const newHours = { ...hours };

    if (section === 'regular') {
      const currentDay = newHours.regular[day as keyof typeof newHours.regular];

      if (field.startsWith('lunchBreak.')) {
        const lunchField = field.split('.')[1] as 'start' | 'end';
        newHours.regular[day as keyof typeof newHours.regular] = {
          ...currentDay,
          lunchBreak: {
            ...(currentDay.lunchBreak || { start: '12:00', end: '14:00' }),
            [lunchField]: newValue as string,
          },
        };
      } else {
        newHours.regular[day as keyof typeof newHours.regular] = {
          ...currentDay,
          [field]: newValue,
        };
      }
    } else if (newHours.special && newHours.special[section]) {
      const currentDay = newHours.special[section].hours[day as keyof typeof newHours.special[0]['hours']];

      if (field.startsWith('lunchBreak.')) {
        const lunchField = field.split('.')[1] as 'start' | 'end';
        newHours.special[section].hours[day as keyof typeof newHours.special[0]['hours']] = {
          ...currentDay,
          lunchBreak: {
            ...(currentDay.lunchBreak || { start: '12:00', end: '14:00' }),
            [lunchField]: newValue as string,
          },
        };
      } else {
        newHours.special[section].hours[day as keyof typeof newHours.special[0]['hours']] = {
          ...currentDay,
          [field]: newValue,
        };
      }
    }

    updateHours(newHours);
  };

  const toggleLunchBreak = (section: 'regular' | number, day: string) => {
    const newHours = { ...hours };

    if (section === 'regular') {
      const currentDay = newHours.regular[day as keyof typeof newHours.regular];
      if (currentDay.lunchBreak) {
        delete currentDay.lunchBreak;
      } else {
        currentDay.lunchBreak = { start: '12:00', end: '14:00' };
      }
    } else if (newHours.special && newHours.special[section]) {
      const currentDay = newHours.special[section].hours[day as keyof typeof newHours.special[0]['hours']];
      if (currentDay.lunchBreak) {
        delete currentDay.lunchBreak;
      } else {
        currentDay.lunchBreak = { start: '12:00', end: '14:00' };
      }
    }

    updateHours(newHours);
  };

  const copyToAllDays = (section: 'regular' | number, sourceDay: string) => {
    const newHours = { ...hours };
    let sourceDayHours: DayHours;

    if (section === 'regular') {
      sourceDayHours = newHours.regular[sourceDay as keyof typeof newHours.regular];
    } else {
      const specialPeriod = newHours.special![section];
      sourceDayHours = specialPeriod.hours[sourceDay as keyof typeof specialPeriod.hours];
    }

    DAYS.forEach(({ key }) => {
      if (section === 'regular') {
        newHours.regular[key as keyof typeof newHours.regular] = { ...sourceDayHours };
      } else {
        const specialPeriod = newHours.special![section];
        specialPeriod.hours[key as keyof typeof specialPeriod.hours] = { ...sourceDayHours };
      }
    });

    updateHours(newHours);
  };

  const applyWeekdaySchedule = () => {
    const weekdayHours = { open: '09:00', close: '18:00', closed: false };
    const saturdayHours = { open: '10:00', close: '14:00', closed: false };
    const sundayHours = { open: '09:00', close: '18:00', closed: true };

    updateHours({
      ...hours,
      regular: {
        monday: weekdayHours,
        tuesday: weekdayHours,
        wednesday: weekdayHours,
        thursday: weekdayHours,
        friday: weekdayHours,
        saturday: saturdayHours,
        sunday: sundayHours,
      },
    });
  };

  const addSpecialPeriod = () => {
    const newSpecial: SpecialPeriod = {
      id: Date.now().toString(),
      name: 'Periodo Speciale',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      hours: { ...defaultRegularHours },
    };

    updateHours({
      ...hours,
      special: [...(hours.special || []), newSpecial],
    });
    setEditingSpecialIndex((hours.special || []).length);
  };

  const deleteSpecialPeriod = (index: number) => {
    if (!hours.special) return;
    const newSpecial = hours.special.filter((_, i) => i !== index);
    updateHours({ ...hours, special: newSpecial });
    setEditingSpecialIndex(null);
  };

  const updateSpecialPeriodInfo = (index: number, field: 'name' | 'startDate' | 'endDate', value: string) => {
    if (!hours.special) return;
    const newSpecial = [...hours.special];
    newSpecial[index] = { ...newSpecial[index], [field]: value };
    updateHours({ ...hours, special: newSpecial });
  };

  const renderDayRow = (section: 'regular' | number, day: typeof DAYS[number], dayHours: DayHours) => (
    <div key={day.key} className={styles.dayRow}>
      <div className={styles.dayLabel}>
        <label>{day.label}</label>
      </div>

      <div className={styles.dayControls}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={dayHours.closed}
            onChange={(e) => handleDayChange(section, day.key, 'closed', e.target.checked)}
          />
          <span>Chiuso</span>
        </label>

        {!dayHours.closed && (
          <>
            <div className={styles.timeInputs}>
              <label className={styles.timeLabel}>
                Apertura:
                <input
                  type="time"
                  value={dayHours.open}
                  onChange={(e) => handleDayChange(section, day.key, 'open', e.target.value)}
                  className={styles.timeInput}
                />
              </label>

              {dayHours.lunchBreak && (
                <label className={styles.timeLabel}>
                  Pausa da:
                  <input
                    type="time"
                    value={dayHours.lunchBreak.start}
                    onChange={(e) => handleDayChange(section, day.key, 'lunchBreak.start', e.target.value)}
                    className={styles.timeInput}
                  />
                </label>
              )}

              {dayHours.lunchBreak && (
                <label className={styles.timeLabel}>
                  a:
                  <input
                    type="time"
                    value={dayHours.lunchBreak.end}
                    onChange={(e) => handleDayChange(section, day.key, 'lunchBreak.end', e.target.value)}
                    className={styles.timeInput}
                  />
                </label>
              )}

              <label className={styles.timeLabel}>
                Chiusura:
                <input
                  type="time"
                  value={dayHours.close}
                  onChange={(e) => handleDayChange(section, day.key, 'close', e.target.value)}
                  className={styles.timeInput}
                />
              </label>
            </div>

            <button
              type="button"
              className={dayHours.lunchBreak ? styles.lunchButtonActive : styles.lunchButton}
              onClick={() => toggleLunchBreak(section, day.key)}
              title={dayHours.lunchBreak ? 'Rimuovi pausa pranzo' : 'Aggiungi pausa pranzo'}
            >
              🍽️
            </button>

            <button
              type="button"
              className={styles.copyButton}
              onClick={() => copyToAllDays(section, day.key)}
              title="Copia questo orario a tutti i giorni"
            >
              📋
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <label className={styles.mainLabel}>Orari di Apertura Regolari</label>
        <div className={styles.quickActions}>
          <button
            type="button"
            className={styles.quickButton}
            onClick={applyWeekdaySchedule}
            title="Applica orario standard: Lun-Ven 9-18, Sab 10-14, Dom chiuso"
          >
            📅 Standard
          </button>
        </div>
      </div>

      <div className={styles.daysContainer}>
        {DAYS.map((day) => renderDayRow('regular', day, hours.regular[day.key as keyof typeof hours.regular]))}
      </div>

      <div className={styles.specialSection}>
        <div className={styles.specialHeader}>
          <h3>Orari Speciali</h3>
          <button
            type="button"
            className={styles.quickButton}
            onClick={() => setShowSpecialPeriods(!showSpecialPeriods)}
          >
            {showSpecialPeriods ? '▼ Nascondi' : '▶ Mostra'} Periodi Speciali
          </button>
        </div>

        {showSpecialPeriods && (
          <div className={styles.specialContent}>
            <p className={styles.specialDesc}>
              Configura orari diversi per periodi specifici (es. festività, ferie estive, etc.)
            </p>

            {hours.special && hours.special.length > 0 && (
              <div className={styles.specialList}>
                {hours.special.map((period, index) => (
                  <div key={period.id} className={styles.specialPeriod}>
                    <div className={styles.specialPeriodHeader}>
                      <input
                        type="text"
                        value={period.name}
                        onChange={(e) => updateSpecialPeriodInfo(index, 'name', e.target.value)}
                        className={styles.specialNameInput}
                        placeholder="Nome periodo (es. Natale 2024)"
                      />
                      <div className={styles.specialDates}>
                        <input
                          type="date"
                          value={period.startDate}
                          onChange={(e) => updateSpecialPeriodInfo(index, 'startDate', e.target.value)}
                          className={styles.dateInput}
                        />
                        <span>→</span>
                        <input
                          type="date"
                          value={period.endDate}
                          onChange={(e) => updateSpecialPeriodInfo(index, 'endDate', e.target.value)}
                          className={styles.dateInput}
                        />
                      </div>
                      <div className={styles.specialActions}>
                        <button
                          type="button"
                          className={styles.editButton}
                          onClick={() => setEditingSpecialIndex(editingSpecialIndex === index ? null : index)}
                        >
                          {editingSpecialIndex === index ? '▼' : '▶'}
                        </button>
                        <button
                          type="button"
                          className={styles.deleteButton}
                          onClick={() => deleteSpecialPeriod(index)}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    {editingSpecialIndex === index && (
                      <div className={styles.daysContainer}>
                        {DAYS.map((day) => renderDayRow(index, day, period.hours[day.key as keyof typeof period.hours]))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              className={styles.addSpecialButton}
              onClick={addSpecialPeriod}
            >
              ➕ Aggiungi Periodo Speciale
            </button>
          </div>
        )}
      </div>

      <div className={styles.preview}>
        <strong>Anteprima Orari Regolari:</strong>
        <div className={styles.previewContent}>
          {DAYS.map(({ key, label }) => {
            const dayHours = hours.regular[key as keyof typeof hours.regular];
            return (
              <div key={key} className={styles.previewRow}>
                <span className={styles.previewDay}>{label}:</span>
                <span className={styles.previewHours}>
                  {dayHours.closed
                    ? 'Chiuso'
                    : dayHours.lunchBreak
                    ? `${dayHours.open}-${dayHours.lunchBreak.start}, ${dayHours.lunchBreak.end}-${dayHours.close}`
                    : `${dayHours.open} - ${dayHours.close}`
                  }
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

OpeningHoursEditor.displayName = 'OpeningHoursEditor';
